import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import { useIntl } from 'react-intl'
import { usePasswordSetupMutation } from '../Auth.api'
import { selectAuth } from '../Auth.slice'
import { useAppSelector } from '../../store/Store'

const initialValues = {
  organization: '',
  email: '',
  password: '',
  passwordCheck: ''
}

export function PasswordSetup() {
  const intl = useIntl()
  const [isComplete, setIsComplete] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState(false)
  const [passwordSetup] = usePasswordSetupMutation()
  const Auth = useAppSelector(selectAuth)

  const passwordSetupSchema = Yup.object().shape({
    organization: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(255, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 255}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    email: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    password: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
    passwordCheck: Yup.string()
      .min(3, intl.formatMessage({id: 'INPUT.VALIDATION.MIN_LENGTH'}, {min: 3}))
      .max(50, intl.formatMessage({id: 'INPUT.VALIDATION.MAX_LENGTH'}, {max: 50}))
      .required(intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED'})),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSetupSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setIsLoading(true)
      try {
        const response: any = await passwordSetup({
          organization: values.organization,
          email: values.email,
          password: values.password,
          passwordCheck: values.passwordCheck,
          session : Auth?.session,
        })

        if (response && response.error) {
          const {error} = response
          switch (error.status) {
            case 401:
              setStatus(intl.formatMessage({id: 'ERROR.MESSAGE.WRONG_CREDENTIALS'}))
              break
            case 500:
              setStatus(intl.formatMessage({id: 'ERROR.MESSAGE.SERVICE_UNAVAILABLE'}))
              break
            default:
              setStatus(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
          }
          setHasErrors(true)
          setIsComplete(false)
        } else {
          setHasErrors(false)
          setIsComplete(true)
          setStatus(intl.formatMessage({id: 'AUTH.PASSWORD_SETUP.SUCCESS_MESSAGE'}))
        }
      } catch (e: any) {
        setHasErrors(true)
        setIsComplete(false)
        setStatus(intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'}))
      } finally {
        setSubmitting(false)
        setIsLoading(false)
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-row-auto'>
      <div className='d-flex flex-column-fluid flex-start'>
        <h1 className='text-white text-left mb-2'>
          {intl.formatMessage({id: 'AUTH.PASSWORD_SETUP.TITLE'})}
        </h1>
      </div>
      <div className='d-flex flex-column-fluid flex-left'>
        <div className='text-white text-left mb-5'>
          {intl.formatMessage({id: 'AUTH.PASSWORD_SETUP.DESCRIPTION'})}
        </div>
      </div>

      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className='d-flex flex-column-fluid flex-center'>
          <div className='d-flex flex-column flex-row-fluid'>
            <div className='d-flex flex-column-auto flex-center'>
              {formik.status && hasErrors && (
                <div className='mb-5 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{formik.status}</div>
                </div>
              )}
              {formik.status && !hasErrors && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>{formik.status}</div>
                </div>
              )}
            </div>

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-building icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'ORGANIZATION'})}
                  {...formik.getFieldProps('organization')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.organization && formik.errors.organization,
                    },
                    {
                      'is-valid': formik.touched.organization && !formik.errors.organization,
                    }
                  )}
                  type='text'
                  name='organization'
                  autoComplete='off'
                />
                {formik.touched.organization && formik.errors.organization && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.organization}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-user-circle icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'EMAIL'})}
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.email && formik.errors.email,
                    },
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                  type='text'
                  name='email'
                  autoComplete='off'
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.email}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-key icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'NEW_PASSWORD'})}
                  {...formik.getFieldProps('password')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.password && formik.errors.password,
                    },
                    {
                      'is-valid': formik.touched.password && !formik.errors.password,
                    }
                  )}
                  type='password'
                  name='password'
                  autoComplete='off'
                />
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.password}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='d-flex flex-column-auto flex-center'>
              <div className='form fv-row mb-5'>
                <i className='text-white form-control-icon fa fa-key icon font-weight-bold'></i>
                <input
                  placeholder={intl.formatMessage({id: 'CONFIRM_PASSWORD'})}
                  {...formik.getFieldProps('passwordCheck')}
                  className={clsx(
                    'placeholder-gray-400 form-control form-control-auth bg-transparent',
                    {
                      'is-invalid': formik.touched.passwordCheck && formik.errors.passwordCheck,
                    },
                    {
                      'is-valid': formik.touched.passwordCheck && !formik.errors.passwordCheck,
                    }
                  )}
                  type='password'
                  name='passwordCheck'
                  autoComplete='off'
                />
                {formik.touched.passwordCheck && formik.errors.passwordCheck && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.passwordCheck}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className='d-flex flex-row mt-2'>
          <div className='d-flex flex-column flex-row-fluid'>
            <button
              type='submit'
              className='btn btn-sm btn-light'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              {!isLoading && (
                <span className='indicator-label'>
                  {intl.formatMessage({id: 'SUBMIT'}).toUpperCase()}
                </span>
              )}
              {isLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>

      <div className='d-flex flex-row flex-center mt-2'>
        <span className='text-white me-2'>{intl.formatMessage({id: 'AUTH.SIGN_IN.BACK_TO'})}</span>{' '}
        <Link to='/auth' className='link-primary text-white fw-semibold '>
          <u>{intl.formatMessage({id: 'AUTH.SIGN_IN.TITLE'})}</u>
        </Link>
      </div>
    </div>
  )
}
